import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	ClickAwayListener,
	InputAdornment,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	TextField,
} from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { CatalogDelayCode } from "../models/CatalogDelayCode";

export interface DelayCodeSearchResult {
	id: number;
	name: string;
	path: string;
	node: CatalogDelayCode;
}

interface DelayCodesSearchInputProps {
	allDelayCodes: CatalogDelayCode[];
	onSearchResultClick: (result: DelayCodeSearchResult) => void;
}

const DelayCodesSearchInputInner: React.FC<DelayCodesSearchInputProps> = ({
	allDelayCodes,
	onSearchResultClick,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [results, setResults] = useState<DelayCodeSearchResult[]>([]);
	const [focusedIndex, setFocusedIndex] = useState(-1);

	const codeMap = useMemo(() => {
		const codes = Array.isArray(allDelayCodes) ? allDelayCodes : [];
		return new Map(
			codes
				.filter((c) => c.DelayCodeId !== null)
				.map((c) => [c.DelayCodeId as number, c]),
		);
	}, [allDelayCodes]);

	const buildPath = useCallback(
		(node: CatalogDelayCode): string => {
			const parts: string[] = [];
			const visited = new Set<number>();
			let cur: CatalogDelayCode | undefined = node;
			while (cur?.ParentDelayCodeId) {
				if (visited.has(cur.ParentDelayCodeId)) break; // cycle protection
				visited.add(cur.ParentDelayCodeId);
				const parent = codeMap.get(cur.ParentDelayCodeId);
				if (parent) {
					parts.unshift(parent.DelayCodeName);
					cur = parent;
				} else break;
			}
			parts.push(node.DelayCodeName);
			return parts.join(" \u203A ");
		},
		[codeMap],
	);

	const searchNodes = useCallback(
		(term: string): DelayCodeSearchResult[] => {
			if (!term.trim() || term.length < 2) return [];
			const lower = term.toLowerCase();
			const codes = Array.isArray(allDelayCodes) ? allDelayCodes : [];
			const matched: DelayCodeSearchResult[] = [];
			for (const c of codes) {
				if (
					c.DelayCodeId !== null &&
					c.ParentDelayCodeId !== null &&
					c.DelayCodeName?.toLowerCase().includes(lower)
				) {
					matched.push({
						id: c.DelayCodeId as number,
						name: c.DelayCodeName,
						path: buildPath(c),
						node: c,
					});
				}
			}
			return matched;
		},
		[allDelayCodes, buildPath],
	);

	const clearResults = useCallback(() => {
		setResults([]);
		setFocusedIndex(-1);
	}, []);

	const handleSelect = useCallback(
		(result: DelayCodeSearchResult) => {
			onSearchResultClick(result);
			if (inputRef.current) inputRef.current.value = "";
			clearResults();
		},
		[onSearchResultClick, clearResults],
	);

	const handleInput = useCallback(() => {
		const val = inputRef.current?.value ?? "";
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (!val.trim() || val.length < 2) {
			clearResults();
			return;
		}
		debounceRef.current = setTimeout(() => {
			const found = searchNodes(val);
			setResults(found);
			setFocusedIndex(found.length > 0 ? 0 : -1);
		}, 300);
	}, [searchNodes, clearResults]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === "Escape") {
				if (inputRef.current) inputRef.current.value = "";
				clearResults();
				return;
			}
			if (results.length === 0) return;
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setFocusedIndex((p) => (p < results.length - 1 ? p + 1 : 0));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setFocusedIndex((p) => (p > 0 ? p - 1 : results.length - 1));
			} else if (e.key === "Enter" && focusedIndex >= 0) {
				e.preventDefault();
				handleSelect(results[focusedIndex]);
			}
		},
		[results, focusedIndex, handleSelect, clearResults],
	);

	return (
		<ClickAwayListener onClickAway={clearResults}>
			<Box sx={{ position: "relative" }} onKeyDown={handleKeyDown}>
				<TextField
					inputRef={inputRef}
					placeholder="Search delay codes (min. 2 characters)..."
					variant="outlined"
					size="small"
					fullWidth
					onInput={handleInput}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						},
					}}
				/>
				{results.length > 0 && (
					<Paper
						elevation={4}
						sx={{
							position: "absolute",
							top: "100%",
							left: 0,
							right: 0,
							zIndex: 1300,
							maxHeight: 300,
							overflowY: "auto",
							mt: 0.25,
						}}
					>
						<List dense>
							{results.map((result, index) => (
								<ListItemButton
									key={result.id}
									selected={index === focusedIndex}
									onClick={() => handleSelect(result)}
								>
									<ListItemIcon sx={{ minWidth: 36 }}>
										<AccountTreeIcon color="primary" fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={result.name}
										secondary={result.path}
										secondaryTypographyProps={{
											variant: "caption",
											color: "textSecondary",
											sx: { fontStyle: "italic" },
										}}
									/>
								</ListItemButton>
							))}
						</List>
					</Paper>
				)}
			</Box>
		</ClickAwayListener>
	);
};

export const DelayCodesSearchInput = React.memo(
	DelayCodesSearchInputInner,
	(prev, next) => prev.allDelayCodes === next.allDelayCodes,
);
